import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { StatusComponent as Status } from "@/components/ramps/Status";
import { useAnalytics } from "@/hooks/usePostHog";
import { convertStoreToStatusDetails } from "@/lib/status-utils";
import { useRampsStore } from "../stores/use-ramps-store";
import type { ExitEventProperties } from "../utils/analytics";
import { ANALYTICS_EVENTS } from "../utils/analytics";

export const Route = createFileRoute("/ramps/status")({
	component: RouteComponent,
});

function RouteComponent() {
	const store = useRampsStore();
	const { trackPageView, trackExit, trackEvent } = useAnalytics();
	const statusDetails = convertStoreToStatusDetails(store);

	// Track page view
	useEffect(() => {
		trackPageView("status", { page_name: "status" });
	}, [trackPageView]);

	// Track transaction completion/exit based on status
	useEffect(() => {
		const status = statusDetails?.transactionStatus;

		if (status === "COMPLETED" || status === "SUCCESS") {
			// Track successful completion
			const exitProps: ExitEventProperties = {
				exit_type: "completed",
				transaction_id: store.transactionId || statusDetails.referenceNumber,
				transaction_status: status,
				exit_reason: "transaction_completed_successfully",
			};
			trackExit("completed", exitProps);
			// Flow-specific success events (in-flow status page; primary is standalone /transaction-status)
			const refNumber = statusDetails?.referenceNumber ?? "";
			if (store.operation === "deposit") {
				trackEvent(ANALYTICS_EVENTS.FUNDS_DEPOSITED, {
					...exitProps,
					reference_number: refNumber,
				});
			} else {
				trackEvent(ANALYTICS_EVENTS.FUNDS_COLLECTED, {
					...exitProps,
					reference_number: refNumber,
				});
			}
		} else if (
			status === "CANCELLED" ||
			status === "CANCELED" ||
			status === "FAILED"
		) {
			// Track cancellation/failure
			const exitProps: ExitEventProperties = {
				exit_type: "cancelled",
				transaction_id: store.transactionId || statusDetails.referenceNumber,
				transaction_status: status,
				exit_reason: "transaction_cancelled_or_failed",
			};
			trackExit("cancelled", exitProps);
		}
	}, [
		statusDetails?.transactionStatus,
		store.transactionId,
		store.operation,
		statusDetails?.referenceNumber,
		trackExit,
		trackEvent,
	]);

	return (
		<div
			className="flex min-h-dvh flex-col"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			<Status
				statusDetails={statusDetails}
				activeTransactionId={store.transactionId}
				onCancelTransaction={() => console.log("Cancel transaction")} // Todo: is there a case where we will allow users to cancel from the review screen? Might not be possible because the status won't be updated to pending_user_transfer_complete until after they leave this page
			/>
		</div>
	);
}
